import { Popover } from '@compass/components'
import { cn, focusRing } from '@compass/utils'
import { useCurrentSubscription } from 'features/subscription'
import { useSuspectScores } from 'hooks/api/suspect_score'
import { InfoIcon } from 'lucide-react'
import type { IdEventV4 } from 'models/event'
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { PreviewTrigger } from 'react-aria-components/PreviewTrigger'

import { calculateSuspectScoreBreakdown } from './suspectScoreBreakdown'
import { SuspectScoreBreakdownContent } from './SuspectScoreBreakdownContent'

export function SuspectScoreBreakdownPopover({ event, children }: PropsWithChildren<{ event?: IdEventV4 }>) {
  /**
   * Conditinally toggle RAC `shouldFlip`
   *
   * We need to revent Popover flipping once user has interacted with Buttons within.
   * This is because height of Popover increases due to content appending on interaction.
   * Reset the state when Popover has closed.
   */
  const [hasInteracted, setHasInteracted] = useState(false)

  function handleInteract() {
    setHasInteracted(true)
  }

  function handleOpenChange(isOpen: boolean) {
    if (isOpen) {
      setHasInteracted(false)
    }
  }

  return (
    <PreviewTrigger onOpenChange={handleOpenChange}>
      <AriaButton
        aria-label='Show Suspect Score breakdown'
        data-feature-highlight='suspect-score-breakdown'
        className={({ isFocusVisible }) =>
          cn(focusRing({ isFocusVisible }), 'inline-flex cursor-default items-center gap-1')
        }
      >
        {children}
        <InfoIcon size={12} className='text-gray-800' />
      </AriaButton>
      <Popover
        placement='top start'
        shouldUpdatePosition={!hasInteracted}
        showArrow={false}
        className='max-w-96 overflow-y-auto overscroll-none'
      >
        <BreakdownForEvent event={event} onInteract={handleInteract} />
      </Popover>
    </PreviewTrigger>
  )
}

function BreakdownForEvent({ event, onInteract }: { event?: IdEventV4; onInteract?: () => void }) {
  const { currentSubscriptionId } = useCurrentSubscription()
  const { data: weights, isLoading } = useSuspectScores(currentSubscriptionId)
  const [isDelayDone, setIsDelayDone] = useState(!isLoading)

  useEffect(() => {
    if (isDelayDone) {
      return
    }
    const timeout = setTimeout(() => setIsDelayDone(true), 800)
    return () => clearTimeout(timeout)
  }, [isDelayDone])

  const breakdown = useMemo(
    () => (event && weights ? calculateSuspectScoreBreakdown(event, weights.scores, weights.updatedAt) : undefined),
    [event, weights]
  )

  return (
    <SuspectScoreBreakdownContent
      subscriptionId={currentSubscriptionId}
      event={event}
      breakdown={breakdown}
      isLoading={isLoading || !isDelayDone}
      onInteract={onInteract}
    />
  )
}
